import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';

// Solo se ofrece cuando el fallo previo fue de scraping (ScraperError) —
// pegar texto no ayuda si lo que falló fue el análisis LLM o el scoring,
// problemas distintos. El propio LeadDetail.js decide cuándo mostrar el
// botón que abre este modal (status===ERROR && last_error_stage==='scrape').
export function ManualContentModal({ open, onOpenChange, leadId, onAnalyzed }) {
  const { api } = useAuth();
  const { t } = useTranslation();

  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!content.trim()) return;

    setSubmitting(true);
    try {
      // POST /leads/{id}/analyze con manual_content — el backend se salta
      // crawl_website() por completo y corre el resto del pipeline
      // (analyze, score, discard, mensajes, status) igual que con un
      // scrape real.
      await api.post(`/leads/${leadId}/analyze`, { manual_content: content.trim() });
      toast.success(t('aiLeadScanner.leadDetail.actionSuccess'));
      setContent('');
      onOpenChange(false);
      onAnalyzed?.();
    } catch (error) {
      console.error('Failed to analyze with manual content:', error);
      toast.error(t('aiLeadScanner.leadDetail.actionError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('aiLeadScanner.leadDetail.pasteManualContentTitle')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-zinc-400">
            {t('aiLeadScanner.leadDetail.pasteManualContentDescription')}
          </p>

          <div>
            <Label>{t('aiLeadScanner.leadDetail.pasteManualContentLabel')}</Label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={10}
              className="mt-1"
              data-testid="manual-content-textarea"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
              {t('aiLeadScanner.leadDetail.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={submitting || !content.trim()} data-testid="manual-content-submit">
              {submitting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              {t('aiLeadScanner.leadDetail.reanalyze')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
